import React, { useState, useRef } from 'react';
import { Search, UserPlus, Loader2, X, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { searchPatients, queueWalkin, queueWalkinRegister } from '@/lib/api';
import { displayAge } from '@/lib/patientAge';
import { formatDate } from '@/lib/formatDate';
import { cn } from '@/lib/utils';

interface Doctor {
  id: string;
  name: string;
}

interface Props {
  doctors: Doctor[];
  effectiveDoctorId: string | null;
  clinicId: string;
  onAdded: () => void;
  /** What was typed (name or phone) and the doctor picked to queue with. Absent without patients:write */
  onRegisterNew?: (search: string, doctorId: string | null) => void;
}

function drName(name: string) {
  return name.startsWith('Dr.') ? name : `Dr. ${name}`;
}

export default function QueueAddSearch({
  doctors,
  effectiveDoctorId,
  clinicId,
  onAdded,
  onRegisterNew,
}: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [isPriority, setIsPriority] = useState(false);
  const [doctorForAdd, setDoctorForAdd] = useState<string>(effectiveDoctorId ?? doctors[0]?.id ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const handleChange = (val: string) => {
    setQuery(val);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchPatients(val, clinicId);
        setResults(Array.isArray(res) ? res.slice(0, 8) : []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 280);
  };

  const resolvedDoctorId = effectiveDoctorId ?? doctorForAdd;

  const handleAdd = async (patient: any) => {
    if (!resolvedDoctorId) {
      toast({ title: 'Select a doctor first', variant: 'destructive' });
      return;
    }
    setAddingId(patient.id);
    try {
      await queueWalkin(patient.id, resolvedDoctorId, clinicId, isPriority);
      toast({ title: 'Added to queue', description: `${patient.name} added` });
      setQuery('');
      setResults([]);
      setOpen(false);
      setIsPriority(false);
      onAdded();
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message ?? 'Failed', variant: 'destructive' });
    } finally {
      setAddingId(null);
    }
  };

  const handleRegister = () => {
    const search = query.trim();
    setQuery('');
    setResults([]);
    setOpen(false);
    onRegisterNew?.(search, resolvedDoctorId || null);
  };

  const clear = () => { setQuery(''); setResults([]); setOpen(false); };

  const showDropdown = open && query.length >= 2;
  const needsDoctorPick = !effectiveDoctorId && doctors.length > 1;

  return (
    <div ref={wrapRef} className="relative p-2 border-b border-border">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={e => handleChange(e.target.value)}
          onFocus={() => query.length >= 2 && setOpen(true)}
          placeholder="Add patient to queue..."
          className="w-full h-8 touch:h-10 pl-8 pr-8 text-base md:text-xs border border-field rounded-md bg-background focus:outline-none focus:border-ring"
        />
        {query && (
          <button onClick={clear} aria-label="Clear search" className="touch-target absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute left-2 right-2 top-full mt-1 bg-popover border border-border rounded-lg shadow-lg z-50 overflow-hidden">
          {/* Doctor picker when in "All" mode */}
          {needsDoctorPick && (
            <div className="px-3 py-2 border-b border-border bg-muted/30 flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Add to:</span>
              <select
                value={doctorForAdd}
                onChange={e => setDoctorForAdd(e.target.value)}
                className="h-7 touch:h-9 text-xs border border-field rounded px-1.5 bg-background flex-1"
              >
                {doctors.map(d => (
                  <option key={d.id} value={d.id}>{drName(d.name)}</option>
                ))}
              </select>
            </div>
          )}

          {/* Priority toggle */}
          <div className="px-3 py-2 border-b border-border flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
              <div className="relative flex-shrink-0">
                <input
                  type="checkbox"
                  checked={isPriority}
                  onChange={e => setIsPriority(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-7 h-4 rounded-full bg-muted peer-checked:bg-warning transition-colors" />
                <div className="absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-3" />
              </div>
              <span className={cn('flex items-center gap-1', isPriority ? 'text-warning font-medium' : 'text-muted-foreground')}>
                <Star className="w-3 h-3" /> Priority
              </span>
            </label>
          </div>

          {/* Results */}
          {searching ? (
            <div className="flex items-center gap-2 p-3 text-xs text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching...
            </div>
          ) : results.length > 0 ? (
            <div className="max-h-52 overflow-y-auto">
              {results.map(p => (
                <div key={p.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-accent border-b border-border/50 last:border-0">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold truncate">{p.name}</p>
                    <p className="text-2xs text-muted-foreground">
                      {[displayAge(p.age, p.date_of_birth), p.gender, p.phone,
                        p.last_visit ? `Last seen ${formatDate(p.last_visit)}` : null].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="h-6 text-2xs px-2 ml-2 flex-shrink-0"
                    disabled={addingId === p.id}
                    onClick={() => handleAdd(p)}
                  >
                    {addingId === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : '+ Queue'}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-3 py-3 text-xs text-muted-foreground">
              No patient found for &quot;{query}&quot;
            </div>
          )}

          {/* Register new */}
          {onRegisterNew && (
            <div className="p-2 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                className="w-full h-7 text-xs gap-1.5"
                onClick={handleRegister}
              >
                <UserPlus className="w-3.5 h-3.5" />
                Register new patient
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
